import React, { forwardRef, useState, useRef, useEffect, type HTMLAttributes, type PropsWithChildren } from 'react';

import { css } from '@emotion/react';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import type { DataElementPageLocation } from '../decorate/decorate';

export type ImageAspectRatioTypes = '1:1' | '4:3' | '16:9' | '49:19' | '85:33';

export interface ImageWithPlaceholderProps extends HTMLAttributes<HTMLImageElement> {
  /** The alt string for the image. */
  alt: string;
  /** Sets the aspect ratio for the ImageWithPlaceholder component. */
  aspectRatio?: ImageAspectRatioTypes;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Checks if the image should be a background image. */
  isBackgroundImage?: boolean;
  /** Checks if the position of the image is on the left of content and applies styles. */
  isLeft?: boolean;
  /** The image src url. */
  src: string;
  /** The title for the ImageWithPlaceholder. */
  title?: string;
  /** Image Page Location */
  dataElementPageLocation?: DataElementPageLocation;
}

const isLoadingCSS = (theme: Lucid.Theme) =>
  css({
    background: `linear-gradient(to right, ${theme.colors.coolGrey[5]} 10%, ${theme.colors.coolGrey[10]} 33%, ${theme.colors.coolGrey[5]} 66%)`,
    backgroundSize: '200% 100%',
    transition: 'opacity 0.25s',
    animation: `${theme.keyframe.loading} 2s ease infinite`,
    animationTimingFunction: 'linear',
  });

const ImageWithPlaceholder = forwardRef<HTMLImageElement, PropsWithChildren<ImageWithPlaceholderProps>>(
  (
    {
      alt,
      aspectRatio = '16:9',
      children,
      className,
      isBackgroundImage = false,
      isLeft = false,
      src,
      title,
      dataElementPageLocation,
      ...rest
    },
    forwardedRef,
  ) => {
    const ref = useRef<HTMLImageElement>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const ratio = aspectRatio.split(':');
    const paddingRatio = (parseInt(ratio[1], 10) / parseInt(ratio[0], 10)) * 100;

    const onLoad = () => {
      setIsLoaded(true);
    };

    useEffect(() => {
      if (ref.current && ref.current.complete) {
        onLoad();
      }
    });

    return (
      <Box
        {...rest}
        ref={forwardedRef}
        css={(theme: Lucid.Theme) => [
          {
            label: 'dynamicImageContainer',
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
          },
          isBackgroundImage && {
            '&::before': {
              display: 'block',
              content: '""',
              paddingTop: !isLeft ? `${paddingRatio}%` : 'unset',
            },
          },
          !isBackgroundImage && {
            height: 0,
            paddingBottom: `${paddingRatio}%`,
            '&::after': [
              {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                opacity: 0,
                zIndex: theme.zIndices[2],
              },
              !isLoaded && isLoadingCSS(theme),
            ],
          },
          !isLoaded && {
            '&::after': {
              opacity: 1,
            },
          },
        ]}
        // eslint-disable-next-line react/jsx-sort-props
        className={className}
        data-testid="imageWithPlaceholder"
      >
        {isBackgroundImage ? (
          <div
            css={{
              label: 'backgroundImage',
              backgroundImage: `url("${src}")`,
              height: '100%',
              width: '100%',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundClip: 'content-box',
              position: 'absolute',
              top: 0,
              bottom: 0,
            }}
            // eslint-disable-next-line react/jsx-sort-props
            className="backgroundImage"
          />
        ) : (
          <img
            ref={ref}
            alt={alt}
            css={(theme: Lucid.Theme) => ({
              label: 'image',
              objectFit: 'contain',
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              zIndex: theme.zIndices.z10,
              margin: 'auto',
              width: 'inherit',
              height: '100%',
            })}
            // eslint-disable-next-line react/jsx-sort-props
            className="image"
            data-element-page-location={dataElementPageLocation || 'body'}
            data-element-type="Image"
            loading="lazy"
            onLoad={onLoad}
            src={src}
            title={title}
          />
        )}
      </Box>
    );
  },
);

export { ImageWithPlaceholder };
export default ImageWithPlaceholder;
